import { Reveal } from "@/components/Reveal";
import { partners, partnersSection } from "@/content/partners";

/** Hairline "+" mark sitting on a grid-line intersection (cell top-left corner). */
function PlusMark() {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -left-px -top-px -translate-x-1/2 -translate-y-1/2 text-brand-300"
    >
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <path d="M5.5 0v11M0 5.5h11" stroke="currentColor" strokeWidth="1" />
      </svg>
    </span>
  );
}

const GRID_COLS = "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";

/**
 * Logo wall of partners/customers. The grid lines + "+" intersections render as a
 * separate layer behind the logos and are masked with a radial fade so the
 * hairlines dissipate toward the edges (blending into the page instead of looking
 * like a hard-bordered box). Logos sit on top at full opacity.
 */
export function PartnerWall() {
  if (partners.length === 0) return null;

  return (
    <section className="bg-white py-16 lg:py-20">
      <div className="mx-auto max-w-[1200px] px-6">
        <Reveal className="mx-auto mb-10 max-w-2xl text-center lg:mb-12">
          <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">
            {partnersSection.eyebrow}
          </div>
          <h2 className="text-balance text-3xl font-bold tracking-tight text-brand-900 lg:text-4xl">
            {partnersSection.heading}
          </h2>
          <p className="mt-3 text-brand-500">{partnersSection.sub}</p>
        </Reveal>

        <Reveal>
          <div className="relative">
            {/* Grid-line + "+" overlay, masked to fade out toward the edges. */}
            <div
              aria-hidden="true"
              className="partner-grid-fade pointer-events-none absolute inset-0"
            >
              <ul className={`grid border-l border-t border-brand-200 ${GRID_COLS}`}>
                {partners.map((partner) => (
                  <li
                    key={partner.name}
                    className="relative aspect-[2/1] border-b border-r border-brand-200"
                  >
                    <PlusMark />
                  </li>
                ))}
              </ul>
            </div>

            {/* Logos on top — no borders, crisp at the edges. */}
            <ul className={`relative grid ${GRID_COLS}`}>
              {partners.map((partner) => (
                <li
                  key={partner.name}
                  className="group flex aspect-[2/1] items-center justify-center p-4 transition-colors duration-300 hover:bg-accent-500/[0.05] sm:p-5"
                >
                  {partner.logo ? (
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      loading="lazy"
                      style={{ "--lh": `${partner.h ?? 40}px` } as React.CSSProperties}
                      className="w-auto max-w-[78%] object-contain opacity-70 grayscale transition duration-300 group-hover:opacity-100 group-hover:grayscale-0 max-h-[calc(var(--lh)*0.66)] sm:max-h-[calc(var(--lh)*0.82)] lg:max-h-[var(--lh)]"
                    />
                  ) : (
                    <span className="select-none text-center text-base font-semibold tracking-tight text-brand-400 transition-colors duration-300 group-hover:text-accent-600">
                      {partner.name}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
